// WHAT: Main debug overlay component with draggable and resizable floating panel
// WHY:  Provides developer interface for viewing and filtering debug logs
// DOMAIN: Logging → Debug Overlay
// DEPENDENCIES: useDebugSystem, useLogStore hooks, LogViewer, LogFilters components
// RISKS:
//   - NOTE: Drag and resize handlers may cause performance issues with many logs
//   - NOTE: localStorage persistence may fail in private browsing mode
//   - NOTE: Position validation may not work correctly on all screen sizes
//   - NOTE: User select disabling may interfere with text selection in logs

import { XMarkIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState, useCallback } from "react";

import { useDebugSystem } from "../../hooks/useDebugSystem";
import { useLogStore } from "../../hooks/useLogStore";
import { LogFilters } from "./LogFilters";
import { LogViewer } from "./LogViewer";

/**
 * WHAT:
 *   Main debug overlay component that provides a floating, draggable, and
 *   resizable panel for viewing and filtering debug logs.
 *
 * WHY:
 *   Enables developers to monitor application logs in real-time without
 *   blocking the main interface.
 *
 * BEHAVIOR:
 *   - Displays as floating overlay
 *   - Supports dragging to reposition
 *   - Supports resizing from edges/corners
 *   - Persists position and size in localStorage
 *   - Validates position to stay within viewport
 *   - Shows log viewer and filters
 *   - Supports minimize/maximize
 *
 * RISKS:
 *   - NOTE: Drag and resize handlers may cause performance issues with many logs
 *   - NOTE: localStorage persistence may fail in private browsing mode
 *   - NOTE: Position validation may not work correctly on all screen sizes
 *   - NOTE: User select disabling may interfere with text selection in logs
 *
 * @returns React component for debug overlay or null if not open
 *
 */
export const DebugOverlay: React.FC = () => {
  const { autoScroll, isMinimized, isOpen, maximizeOverlay, minimizeOverlay, toggleOverlay } =
    useDebugSystem();

  const { error, filteredLogs, isLoading } = useLogStore();
  const logViewerReference = useRef<HTMLDivElement>(null);

  // NOTE: Draggable state
  const [position, setPosition] = useState({ x: 24, y: 24 }); // NOTE: Default: top-6 right-6
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const overlayReference = useRef<HTMLDivElement>(null);

  // NOTE: Resizable state
  const [size, setSize] = useState({ height: 500, width: 384 }); // NOTE: Default: w-96 h-[500px]
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  });
  const [resizeDirection, setResizeDirection] = useState<string>("");

  /**
   * WHAT:
   *   Saves overlay position to localStorage.
   *
   * WHY:
   *   Persists user's preferred overlay position.
   */
  const savePosition = useCallback((newPosition: { x: number; y: number }) => {
    localStorage.setItem("debug-overlay-position", JSON.stringify(newPosition));
  }, []);

  // NOTE: Load position and size from localStorage on first open
  useEffect(() => {
    if (isOpen) {
      const savedPosition = localStorage.getItem("debug-overlay-position");
      const savedSize = localStorage.getItem("debug-overlay-size");

      let loadedSize = { height: 500, width: 384 };
      if (savedSize) {
        try {
          loadedSize = JSON.parse(savedSize) as {
            width: number;
            height: number;
          };
          setSize(loadedSize);
        } catch {
          // NOTE: Failed to parse saved size; fall back to defaults
        }
      }

      // Position validieren und korrigieren, damit Overlay vollständig sichtbar ist
      if (savedPosition) {
        try {
          const parsed = JSON.parse(savedPosition) as {
            x: number;
            y: number;
          };
          let { x, y } = parsed;

          // NOTE: Ensure overlay stays fully visible in viewport
          const minX = 0; // NOTE: At least at left edge
          const maxX = window.innerWidth - loadedSize.width; // NOTE: Not over right edge
          const minY = 0; // NOTE: At least at top edge (header must be visible)
          const maxY = window.innerHeight - loadedSize.height; // NOTE: Not over bottom edge

          // NOTE: Clamp position within viewport
          x = Math.max(minX, Math.min(x, maxX));
          y = Math.max(minY, Math.min(y, maxY));

          setPosition({ x, y });
          // NOTE: Save corrected position
          savePosition({ x, y });
        } catch {
          // NOTE: Fallback: Default position (top right) if persisted position is invalid
          const defaultX = Math.max(0, window.innerWidth - loadedSize.width - 24);
          const defaultY = 24;
          setPosition({ x: defaultX, y: defaultY });
        }
      } else {
        // NOTE: No saved position: Default position (top right)
        const defaultX = Math.max(0, window.innerWidth - loadedSize.width - 24);
        const defaultY = 24;
        setPosition({ x: defaultX, y: defaultY });
      }
    }
  }, [isOpen, savePosition]);

  /**
   * WHAT:
   *   Saves overlay size to localStorage.
   *
   * WHY:
   *   Persists user's preferred overlay size.
   */
  const saveSize = useCallback((newSize: { width: number; height: number }) => {
    localStorage.setItem("debug-overlay-size", JSON.stringify(newSize));
  }, []);

  /**
   * WHAT:
   *   Handles mouse down event for dragging.
   *
   * WHY:
   *   Initiates drag operation when clicking on draggable area.
   *
   * BEHAVIOR:
   *   - Checks if click is on button or non-draggable element
   *   - Only allows dragging from header area
   *   - Calculates drag start offset
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // NOTE: Check if click was on button or non-draggable element
      const target = e.target as HTMLElement;
      if (target.closest("button") || target.closest("[data-no-drag]")) {
        return; // NOTE: Not draggable when clicking on button
      }

      // NOTE: Only draggable when clicking on header area
      if (target.closest('[data-draggable="true"]')) {
        setIsDragging(true);
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        });
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [position],
  );

  /**
   * WHAT:
   *   Handles mouse move event during dragging.
   *
   * WHY:
   *   Updates overlay position while dragging.
   *
   * BEHAVIOR:
   *   - Calculates new position from mouse coordinates
   *   - Clamps position to viewport boundaries
   *   - Saves position to localStorage
   */
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;

        // NOTE: Consider viewport boundaries - overlay must stay fully visible
        const minX = 0; // NOTE: Not over left edge
        const maxX = window.innerWidth - size.width; // NOTE: Not over right edge
        const minY = 0; // NOTE: Header must be visible (not over top edge)
        const maxY = window.innerHeight - size.height; // NOTE: Not over bottom edge

        const clampedX = Math.max(minX, Math.min(newX, maxX));
        const clampedY = Math.max(minY, Math.min(newY, maxY));

        const newPosition = { x: clampedX, y: clampedY };
        setPosition(newPosition);
        savePosition(newPosition);
      }
    },
    [isDragging, dragStart, size, savePosition],
  );

  /**
   * WHAT:
   *   Handles mouse up event to end drag/resize.
   *
   * WHY:
   *   Cleans up drag/resize state.
   */
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection("");
  }, []);

  /**
   * WHAT:
   *   Handles resize start event.
   *
   * WHY:
   *   Initiates resize operation from specific edge/corner.
   *
   * BEHAVIOR:
   *   - Sets resize direction
   *   - Stores initial size and mouse position
   */
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, direction: string) => {
      e.stopPropagation();
      setIsResizing(true);
      setResizeDirection(direction);
      setResizeStart({
        height: size.height,
        width: size.width,
        x: e.clientX,
        y: e.clientY,
      });
    },
    [size],
  );

  /**
   * WHAT:
   *   Handles mouse move event during resizing.
   *
   * WHY:
   *   Updates overlay size while resizing.
   *
   * BEHAVIOR:
   *   - Calculates new size from mouse delta
   *   - Applies size constraints (min/max)
   *   - Handles different resize directions
   *   - Saves size to localStorage
   */
  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (isResizing) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;

        let newWidth = resizeStart.width;
        let newHeight = resizeStart.height;

        // NOTE: Minimum and maximum sizes
        const minWidth = 300;
        const minHeight = 200;
        const maxWidth = window.innerWidth - 50;
        const maxHeight = window.innerHeight - 50;

        if (resizeDirection.includes("right")) {
          newWidth = Math.max(minWidth, Math.min(maxWidth, resizeStart.width + deltaX));
        }
        if (resizeDirection.includes("left")) {
          newWidth = Math.max(minWidth, Math.min(maxWidth, resizeStart.width - deltaX));
        }
        if (resizeDirection.includes("bottom")) {
          newHeight = Math.max(minHeight, Math.min(maxHeight, resizeStart.height + deltaY));
        }
        if (resizeDirection.includes("top")) {
          newHeight = Math.max(minHeight, Math.min(maxHeight, resizeStart.height - deltaY));
        }

        const newSize = { height: newHeight, width: newWidth };
        setSize(newSize);
        saveSize(newSize);
      }
    },
    [isResizing, resizeStart, resizeDirection, saveSize],
  );

  // NOTE: Global mouse events for dragging and resizing
  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener("mousemove", isDragging ? handleMouseMove : handleResizeMove);
      document.addEventListener("mouseup", handleMouseUp);
      // NOTE: Prevent text selection during drag/resize
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", isDragging ? handleMouseMove : handleResizeMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "";
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleResizeMove, handleMouseUp]);

  // Auto-Scroll zu neuen Logs (nach oben, da neue Logs oben stehen)
  useEffect(() => {
    if (logViewerReference.current && !isMinimized && isOpen && autoScroll) {
      logViewerReference.current.scrollTop = 0;
    }
  }, [filteredLogs, isMinimized, isOpen, autoScroll]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed z-[99999]"
      ref={overlayReference}
      style={{
        cursor: isDragging || isResizing ? "grabbing" : "grab",
        height: `${String(size.height)}px`,
        left: `${String(position.x)}px`,
        pointerEvents: "auto",
        top: `${String(position.y)}px`,
        width: `${String(size.width)}px`,
      }}
    >
      <div
        className={`
          bg-gray-900 border border-gray-700 rounded-lg shadow-2xl
          transition-all duration-300 ease-in-out
          ${isMinimized ? "h-12" : "h-full"}
          ${isDragging ? "shadow-3xl scale-105" : ""}
          ${isResizing ? "shadow-3xl" : ""}
        `}
        style={{
          height: "100%",
          pointerEvents: "auto",
          width: "100%",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-3 border-b border-gray-700 cursor-grab active:cursor-grabbing select-none"
          data-draggable="true"
          onMouseDown={handleMouseDown}
          style={{ pointerEvents: "auto" }}
        >
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-white">Debug System</span>
            </div>
            <span className="text-xs text-gray-400">{filteredLogs.length} logs</span>
          </div>

          <div className="flex items-center space-x-1">
            {/* Minimize/Maximize Button */}
            <button
              className="p-1 text-gray-400 hover:text-white transition-colors"
              data-no-drag="true"
              onClick={isMinimized ? maximizeOverlay : minimizeOverlay}
              onMouseDown={(e) => {
                e.stopPropagation();
              }} // Verhindert Dragging
              title={isMinimized ? "Maximieren" : "Minimieren"}
            >
              {isMinimized ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )}
            </button>

            {/* Close Button */}
            <button
              className="p-1 text-gray-400 hover:text-white transition-colors"
              data-no-drag="true"
              onClick={toggleOverlay}
              onMouseDown={(e) => {
                e.stopPropagation();
              }} // Verhindert Dragging
              title="Schließen"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        {!isMinimized && (
          <div
            className="flex flex-col h-[calc(100%-3rem)] w-full"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            {/* Filters */}
            <div className="border-b border-gray-700 flex-shrink-0 w-full">
              <LogFilters />
            </div>

            {/* Log Viewer */}
            <div className="flex-1 min-h-0 w-full overflow-hidden">
              <LogViewer ref={logViewerReference} />
            </div>

            {/* Status Bar */}
            <div
              className="flex items-center justify-between p-2 border-t border-gray-700 bg-gray-800 flex-shrink-0 w-full"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="flex items-center space-x-4 text-xs text-gray-400">
                {isLoading ? (
                  <span className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                    <span>Lädt...</span>
                  </span>
                ) : null}
                {error ? <span className="text-red-400">Fehler: {error}</span> : null}
              </div>

              <div className="text-xs text-gray-400">
                {filteredLogs.length} von {filteredLogs.length} Logs | Auto-Scroll:{" "}
                {autoScroll ? "An" : "Aus"}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Resize Handles */}
      {!isMinimized && (
        <>
          {/* Corner Handles */}
          <div
            className="absolute top-0 left-0 w-3 h-3 cursor-nw-resize bg-blue-500 opacity-0 hover:opacity-100 transition-opacity"
            onMouseDown={(e) => {
              handleResizeStart(e, "top-left");
            }}
          />
          <div
            className="absolute top-0 right-0 w-3 h-3 cursor-ne-resize bg-blue-500 opacity-0 hover:opacity-100 transition-opacity"
            onMouseDown={(e) => {
              handleResizeStart(e, "top-right");
            }}
          />
          <div
            className="absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize bg-blue-500 opacity-0 hover:opacity-100 transition-opacity"
            onMouseDown={(e) => {
              handleResizeStart(e, "bottom-left");
            }}
          />
          <div
            className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize bg-blue-500 opacity-0 hover:opacity-100 transition-opacity"
            onMouseDown={(e) => {
              handleResizeStart(e, "bottom-right");
            }}
          />

          {/* Edge Handles */}
          <div
            className="absolute top-0 left-3 right-3 h-1 cursor-n-resize bg-blue-500 opacity-0 hover:opacity-100 transition-opacity"
            onMouseDown={(e) => {
              handleResizeStart(e, "top");
            }}
          />
          <div
            className="absolute bottom-0 left-3 right-3 h-1 cursor-s-resize bg-blue-500 opacity-0 hover:opacity-100 transition-opacity"
            onMouseDown={(e) => {
              handleResizeStart(e, "bottom");
            }}
          />
          <div
            className="absolute left-0 top-3 bottom-3 w-1 cursor-w-resize bg-blue-500 opacity-0 hover:opacity-100 transition-opacity"
            onMouseDown={(e) => {
              handleResizeStart(e, "left");
            }}
          />
          <div
            className="absolute right-0 top-3 bottom-3 w-1 cursor-e-resize bg-blue-500 opacity-0 hover:opacity-100 transition-opacity"
            onMouseDown={(e) => {
              handleResizeStart(e, "right");
            }}
          />
        </>
      )}
    </div>
  );
};
